/**
 * GraphQL response parser for content data.
 * Transforms raw GraphQL responses into application-specific data structures.
 * Handles parsing for highlights, reels, EPG events, and EPG by days data,
 * including filtering, sorting, and data transformation logic.
 */

import Content from '@shared/types/enums/content';
import lodash from 'lodash';
import EpgEvent from '@shared/classes/epgEvent';
import Rail from '@shared/classes/rail';
import Slider from '@shared/classes/slider';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { IReelData, IReelResponse } from '@shared/types/interfaces/rail';
import { RailDisplayMode } from '@shared/types/enums/rail';
import { store } from '@shared/store/store';
import {
  EventMap,
  IEPGRequest,
  IEPGByDaysRequest,
} from '@shared/types/interfaces/event';
import {
  IHighlightData,
  IHighlightResponse,
} from '@shared/types/interfaces/slider';

/********************/
/**     PARSER     **/
/********************/

/**
 * Data parser object containing methods for parsing different GraphQL response types.
 * Provides parsing functions for highlights, reels, EPG events, and EPG by days.
 */
const parser = {
  /**
   *
   * @param data Highlights raw data
   * @returns Parsed slider object array
   */
  highlights: (response?: IHighlightResponse) => {
    try {
      if (response) {
        const { channels } = store.getState();
        const highlights = (
          lodash.cloneDeep(response.results) as IHighlightData[]
        )
          .filter((slider) => {
            return (
              !slider.channelName ||
              channels.favouriteChannels?.find((ch) => {
                return slider?.channelName === ch.name;
              })
            );
          })
          .filter((slider) => !slider.isCpg)
          .map((item, index) => new Slider(item, index));
        return {
          data: highlights,
          pages: response.totalPages,
        };
      }
    } catch (e) {
      console.error('[GraphQL][Parser][Highlights]', e);
    }
    return null;
  },
  /**
   *
   * @param data Reels raw data
   * @returns Parsed rail object array
   */
  reels: (response?: IReelResponse, horizontalPages?: Map<number, number>) => {
    try {
      if (response) {
        const reels = (lodash.cloneDeep(response.results) as IReelData[])
          .map((item, index) => {
            let data:
              | typeof item.shows
              | typeof item.addons
              | typeof item.iacorners
              | typeof item.corners = [];
            if (item.shows.length) {
              const { channels, user } = store.getState();
              const allChannels = [
                ...(channels.channelsData || []),
                ...(channels.unavailableChannels || []),
              ];
              // Filter content that does not match user's postal code
              data = item.shows.filter((item) => {
                const channel = allChannels.find(
                  (channel) => channel.id === item.channel
                );
                if (user.isAnonymous) {
                  return channel?.postal?.scope === 'nacional';
                } else if (!user.isTest && channel?.postal?.province?.length) {
                  return channel?.postal?.province.includes(
                    user?.postalCode?.substring(0, 2) || ''
                  );
                }
                return true;
              });
            } else if (item.corners?.length) {
              data = item.corners.map((item) => {
                item.kind = Content.CORNER;
                return item;
              });
            } else if (item.iacorners?.length) {
              item.mediaSection.reelIA = true;
              data = item.iacorners.map((item) => {
                item.kind = Content.IA_CORNER;
                return item;
              });
            } else if (item.addons?.length) {
              data = item.addons.map((item) => {
                item.kind = Content.ADDON;
                return item;
              });
            }
            // Build reel
            const reel = {
              section: {
                name: item?.mediaSection.name || '',
                pagination: {
                  currentPage: horizontalPages?.get(index) || 0,
                  totalPages: item?.totalPages || 0,
                  verticalIndex: index,
                  verticalPosition: item?.mediaSection.position ?? index,
                },
                presentationType:
                  item?.mediaSection?.presentationType || RailDisplayMode.PHOTO,
                reelIA: item?.mediaSection?.reelIA,
                title:
                  (item?.mediaSection?.titles
                    ? item?.mediaSection?.titles[
                        TranslationManager.currentLanguage as 'ca' | 'en' | 'es'
                      ]
                    : '') ||
                  item?.mediaSection.name ||
                  '',
              },
              shows: { data },
            };
            const railInstance = new Rail(reel);
            return railInstance;
          }, [])
          // Sort reel vertically based on mediaSection idnex
          .sort(
            (a, b) =>
              (a.pagination?.verticalPosition || 0) -
              (b.pagination?.verticalPosition || 0)
          );

        return {
          data: reels,
          pages: response.totalPages,
        };
      }
    } catch (e) {
      console.error('[GraphQL][Parser][Reels]', e);
    }
    return null;
  },
  /**
   * Parses and processes EPG (Electronic Program Guide) raw data.
   *
   * @param {IEPGRequest[]} [response] - The raw EPG data to be processed.
   * @returns {{ map: EventMap, maxItems: number, maxPages: number } | null}
   *          The parsed event map, including the maximum number of items and pages,
   *          or null if an error occurs.
   */
  epgs: (response?: IEPGRequest, data?: EventMap, filterOldEvents = false) => {
    try {
      // Store number of items & pages and save incoming events into map
      const map: EventMap = data ? lodash.cloneDeep(data) || {} : {};
      let maxItems = 0;
      let maxPages = 1;

      // Include events from response into map
      const date = new Date();
      response?.epgs?.items?.forEach((item) => {
        // If no data is found, create an array for such channel
        if (!map[item.channel]) map[item.channel] = [];
        // Iterate through all epg events
        item.epgs.forEach((epg) => {
          // Push found event if not duplicated
          const event = new EpgEvent(epg);
          if (!map[item.channel].find((ev) => ev.id === event.id)) {
            map[item.channel].push(event);
          }
        });
        // Filter old events and sort them by date
        if (filterOldEvents) {
          map[item.channel] = map[item.channel].filter((event) => {
            return !event?.date || date.getTime() < event.date.end;
          });
        }
        map[item.channel].sort((a, b) => {
          if (a.date?.start && b.date?.start) {
            return a.date.start - b.date.start;
          }
          return 0;
        });
        // Update maxItems and maxPages
        if (item.pages > maxPages) maxPages = item.pages;
        if (item.totalItems > maxItems) maxItems = item.totalItems;
      });
      return { map, maxItems, maxPages };
    } catch (e) {
      console.error('[GraphQL][Parser][Epgs]', e);
    }
    return null;
  },
  /**
   * Parses and processes EPG (Electronic Program Guide) raw data.
   *
   * @param {IEPGRequest[]} [response] - The raw EPG data to be processed.
   * @returns {{ map: EventMap, maxItems: number, maxPages: number } | null}
   *          The parsed event map, including the maximum number of items and pages,
   *          or null if an error occurs.
   */
  epgsByDays: (response?: IEPGByDaysRequest) => {
    try {
      // Store number of items & pages and save incoming events into map
      const map: EventMap = {};
      let maxItems = 0;
      let maxPages = 1;

      // Include events from response into map
      const date = new Date();
      response?.epgsByDays?.items?.forEach((item) => {
        // If no data is found, create an array for such channel
        if (!map[item.day]) map[item.day] = [];
        // Iterate through all epg events
        item.epgs.forEach((epg) => {
          // Push found event if not duplicated
          const event = new EpgEvent(epg);
          if (!map[item.day].find((ev) => ev.id === event.id)) {
            map[item.day].push(event);
          }
        });
        // Filter old events and sort them by date
        map[item.day] = map[item.day]
          .filter((event) => {
            return !event?.date || date.getTime() < event.date.end;
          })
          .sort((a, b) => {
            if (a.date?.start && b.date?.start) {
              return a.date.start - b.date.start;
            }
            return 0;
          });
        // Update maxItems and maxPages
        if (item.pages > maxPages) maxPages = item.pages;
        if (item.totalItems > maxItems) maxItems = item.totalItems;
      });
      return { map, maxItems, maxPages };
    } catch (e) {
      console.error('[GraphQL][Parser][EpgsByDays]', e);
    }
    return null;
  },
};

export default parser;
